import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, SwitchCamera, X, Loader2, RotateCcw, Download, Info, Eye, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ShareButton from '@/components/ShareButton';
import AIEnhancement from '@/components/AIEnhancement';
import ComprehensiveAnalysisResults from '@/components/ComprehensiveAnalysisResults';
import { generateSingleAnalysisWord } from '@/utils/wordGenerator';
import ScrollToTop from '@/components/ScrollToTop';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiveCamera = ({ darkMode, userId, audienceFilters, onAnalysisComplete, user, authToken }) => {
  const [isActive, setIsActive] = useState(false);
  const [facingMode, setFacingMode] = useState('user');
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [liveAnalysis, setLiveAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [showInstructions, setShowInstructions] = useState(true);
  const [showFullResults, setShowFullResults] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1); // 0.5, 1, or 2
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  useEffect(() => {
    if (stream && videoRef.current && !capturedImage) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(err => {
        console.error('Video play error:', err);
      });
    }
  }, [stream, capturedImage]);

  const startCamera = async (facing = facingMode) => {
    try {
      setError(null);
      setCapturedImage(null);
      setLiveAnalysis(null);
      setShowInstructions(true);
      
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const constraints = {
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          facingMode: facing
        },
        audio: false
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      setIsActive(true);
      
      // Hide instructions after 4 seconds
      setTimeout(() => setShowInstructions(false), 4000);
      
    } catch (err) {
      console.error('Camera error:', err);
      setError('Unable to access camera. Please check permissions.');
      toast.error('Camera access denied');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsActive(false);
    setCapturedImage(null);
    setLiveAnalysis(null);
    setError(null);
  };

  const switchCamera = async () => {
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacingMode);
    setZoomLevel(1); // Reset zoom when switching cameras
    await startCamera(newFacingMode);
  };

  const handleZoomChange = (level) => {
    setZoomLevel(level);
  };

  const downloadPhoto = () => {
    if (!capturedImage) return;
    
    const link = document.createElement('a');
    link.href = capturedImage;
    link.download = `emoQ-analysis-${Date.now()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Photo saved to your device!');
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    if (video.readyState !== 4) {
      toast.error('Camera not ready. Please wait a moment.');
      return;
    }
    
    console.log('📸 Capturing photo...');
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    console.log('Photo captured, size:', imageData.length, 'bytes');
    setCapturedImage(imageData);
    
    // Stop camera stream after capture
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    
    toast.info('Photo captured! Analyzing...');
    
    // Analyze the captured photo
    await analyzePhoto(imageData);
  };

  const analyzePhoto = async (imageData) => {
    try {
      setIsAnalyzing(true);
      setError(null);
      
      console.log('Starting photo analysis...');
      console.log('API endpoint:', `${API}/analyze/image`);
      console.log('User ID:', userId);
      console.log('Audience filters:', audienceFilters);
      
      const response = await fetch(imageData);
      const blob = await response.blob();
      console.log('Image converted to blob, size:', blob.size);
      
      const formData = new FormData();
      formData.append('file', blob, 'capture.jpg');
      formData.append('user_id', userId || '');
      formData.append('audience_filters', JSON.stringify(audienceFilters || {}));

      console.log('Sending request to backend...');
      const result = await axios.post(`${API}/analyze/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
        withCredentials: true
      });

      console.log('✅ Analysis result received:', result.data);
      setLiveAnalysis(result.data);
      toast.success('Photo analyzed successfully!');
      
    } catch (error) {
      console.error('❌ Analysis error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      let errorMessage = 'Analysis failed. Please try again.';
      if (error.response?.status === 500) {
        errorMessage = 'Server error during analysis. Please try again.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Analysis timed out. Please try with a smaller image.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    setLiveAnalysis(null);
    setError(null);
    setIsAnalyzing(false);
  };

  const handleDownloadReport = async () => {
    if (!liveAnalysis) return;
    
    try {
      await generateSingleAnalysisWord(liveAnalysis);
      toast.success('Report downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download report');
    }
  };

  const normalizePercentage = (value) => {
    if (typeof value !== 'number') return 0;
    if (value <= 1) return Math.round(value * 100);
    return Math.min(Math.round(value), 100);
  };

  if (!isActive) {
    return (
      <div className="text-center">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg p-12 border-2 border-dashed ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
          <Camera className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Camera Photo Analysis
          </h3>
          <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Take photos and analyze their emotional impact instantly
          </p>
          <Button 
            onClick={() => startCamera()} 
            size="lg"
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Camera className="w-5 h-5 mr-2" />
            Open Camera
          </Button>
        </div>
        
        {error && (
          <div className={`mt-4 p-4 rounded-lg border ${darkMode ? 'bg-red-900/30 border-red-700 text-red-300' : 'bg-red-50 border-red-200 text-red-700'} flex items-center gap-2`}>
            <span className="text-sm">{error}</span>
          </div>
        )}
        
        <div className={`mt-6 p-4 rounded-lg ${darkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'} border`}>
          <div className="flex items-start gap-2 mb-2">
            <Info className={`w-5 h-5 flex-shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <div className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-800'} font-semibold`}>
              How it works:
            </div>
          </div>
          <ul className={`text-xs ${darkMode ? 'text-blue-200' : 'text-blue-700'} space-y-1 ml-7`}>
            <li>📸 Tap the white circle button to take a photo</li>
            <li>🎯 Photo is analyzed for emotional impact</li>
            <li>📊 See detailed emotion breakdown and suggestions</li>
            <li>🔄 Take multiple photos and compare results</li>
            <li>💾 Download photos to your device</li>
          </ul>
        </div>
      </div>
    );
  }

  // Full Results View - Scrollable Page
  if (showFullResults && liveAnalysis) {
    return (
      <div className={`fixed inset-0 z-[9999] overflow-y-auto ${darkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
        {/* Header with back button - Mobile Optimized */}
        <div className={`sticky top-0 z-50 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-2 sm:px-4 py-2 sm:py-3 flex items-center gap-2`}>
          <Button
            onClick={() => setShowFullResults(false)}
            variant="ghost"
            size="sm"
            className="flex items-center gap-1 text-xs sm:text-sm p-1.5 sm:p-2 min-w-0"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden xs:inline text-xs">Back</span>
          </Button>
          <h1 className="text-xs sm:text-base md:text-lg font-bold flex-1 truncate">Live Photo Analysis</h1>
          <X 
            className="w-5 h-5 cursor-pointer hover:opacity-70 flex-shrink-0"
            onClick={() => {
              stopCamera();
              setCapturedImage(null);
              setLiveAnalysis(null);
              setShowFullResults(false);
            }}
          />
        </div>

        {/* Captured Image - Mobile Optimized */}
        {capturedImage && (
          <div className="w-full px-2 sm:px-4 py-2 sm:py-4">
            <img 
              src={capturedImage} 
              alt="Captured" 
              className="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
            />
          </div>
        )}

        {/* Comprehensive Results - Mobile Optimized Container */}
        <div className="w-full px-2 sm:px-4 py-2 sm:py-6 pb-16 max-w-6xl mx-auto" id="analysis-results-pdf">
          <ComprehensiveAnalysisResults
            result={liveAnalysis}
            darkMode={darkMode}
            isLiveAnalysis={true}
          />

          {/* AI Enhancement Section - Mobile Optimized */}
          <div className="mt-3 sm:mt-6 mb-8">
            <AIEnhancement 
              analysis={liveAnalysis} 
              darkMode={darkMode}
              user={user}
              authToken={authToken}
            />
          </div>
        </div>
        
        {/* Scroll to Top Button */}
        <ScrollToTop darkMode={darkMode} />
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-[9999] bg-black overflow-hidden"
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh'
      }}
    >
      {/* Video or Captured Image with Split Layout */}
      {capturedImage ? (
        <div className="absolute inset-0 flex flex-col">
          {/* Captured Image - Top Half (40% of screen) */}
          <div className="relative w-full h-[40vh] bg-black flex items-center justify-center">
            <img 
              src={capturedImage} 
              alt="Captured" 
              className="max-w-full max-h-full object-contain"
            />
          </div>
          
          {/* Analysis Results Area - Bottom Half (60% of screen) */}
          <div className="relative w-full flex-1 bg-gradient-to-b from-gray-900 to-black overflow-y-auto">
            {/* Show loading or results */}
            {isAnalyzing ? (
              <div className="flex flex-col items-center justify-center h-full p-6">
                <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
                <p className="text-white text-lg font-semibold">Analyzing your photo...</p>
                <p className="text-gray-400 text-sm mt-2">This may take a few moments</p>
              </div>
            ) : liveAnalysis ? (
              <div className="p-4 pb-24 space-y-3">
                {/* Dominant emotion */}
                <div className="text-center mb-2">
                  <div className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-full text-xl font-bold shadow-xl">
                    {liveAnalysis.dominant_emotion?.toUpperCase() || 'ANALYZING'}
                  </div>
                </div>
                
                {/* Top 6 Emotions */}
                <div className="bg-gray-900/95 backdrop-blur-md rounded-xl p-3 border border-white/30 shadow-2xl">
                  <div className="text-white font-bold text-sm mb-2">📊 Top Emotions:</div>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(liveAnalysis.emotions || {})
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 6)
                      .map(([emotion, value]) => {
                        const percentage = normalizePercentage(value);
                        return (
                          <div key={emotion} className="bg-gray-800/90 backdrop-blur-sm rounded-lg p-2 border border-white/40 shadow-lg">
                            <div className="text-white text-xs font-semibold capitalize truncate">{emotion}</div>
                            <div className="text-white text-lg font-bold">{percentage}%</div>
                            <div className="w-full bg-white/20 rounded-full h-1.5 mt-1">
                              <div className="bg-blue-400 h-1.5 rounded-full" style={{ width: `${Math.min(percentage, 100)}%` }} />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
                
                {/* PAD Model */}
                <div className="bg-gray-900/95 backdrop-blur-md rounded-xl p-3 border border-white/30 shadow-2xl">
                  <div className="text-white font-bold text-sm mb-2">🎯 PAD Model:</div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-gradient-to-br from-green-500/40 to-green-700/40 rounded-lg p-2 text-center border border-green-400/60 shadow-lg">
                      <div className="text-green-200 text-xs font-semibold">Pleasure</div>
                      <div className="text-white text-xl font-bold">{normalizePercentage(liveAnalysis.valence)}%</div>
                    </div>
                    <div className="bg-gradient-to-br from-orange-500/40 to-red-700/40 rounded-lg p-2 text-center border border-orange-400/60 shadow-lg">
                      <div className="text-orange-200 text-xs font-semibold">Arousal</div>
                      <div className="text-white text-xl font-bold">{normalizePercentage(liveAnalysis.arousal_level)}%</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500/40 to-purple-700/40 rounded-lg p-2 text-center border border-purple-400/60 shadow-lg">
                      <div className="text-purple-200 text-xs font-semibold">Dominance</div>
                      <div className="text-white text-xl font-bold">{normalizePercentage(liveAnalysis.dominance)}%</div>
                    </div>
                  </div>
                </div>
                
                {/* Suggestions */}
                {liveAnalysis.recommendations && liveAnalysis.recommendations.length > 0 && (
                  <div className="bg-gradient-to-r from-yellow-500/50 to-orange-500/50 backdrop-blur-md rounded-xl p-3 border-2 border-yellow-400/80 shadow-2xl">
                    <div className="text-yellow-300 font-bold text-sm mb-2">💡 SUGGESTIONS:</div>
                    <div className="space-y-1">
                      {liveAnalysis.recommendations.slice(0, 3).map((rec, idx) => (
                        <div key={idx} className="text-white text-xs font-medium bg-black/40 rounded-lg p-2 shadow-md">
                          • {rec}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Audience Reactions - Compact */}
                {liveAnalysis.audience_reactions && Object.keys(liveAnalysis.audience_reactions).length > 0 && (
                  <div className="bg-gray-900/95 backdrop-blur-md rounded-xl p-3 border border-white/30 shadow-2xl">
                    <div className="text-white font-bold text-sm mb-2">👥 Audience Reactions:</div>
                    <div className="space-y-1">
                      {Object.entries(liveAnalysis.audience_reactions).slice(0, 4).map(([audience, reaction]) => (
                        <div key={audience} className="bg-gradient-to-r from-purple-600/40 to-indigo-600/40 backdrop-blur-sm rounded-lg p-2 border border-purple-400/50 shadow-lg">
                          <div className="text-purple-200 font-bold text-xs">{audience}</div>
                          <div className="text-white text-xs mt-0.5">{reaction}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Action buttons */}
                <div className="space-y-2 pt-2 pb-24">
                  {/* Download & Share buttons - prominent at top */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={handleDownloadReport}
                      className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white py-4 rounded-xl font-bold"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                    <ShareButton 
                      analysisId={liveAnalysis?.id}
                      userName="Anonymous User"
                      size="lg"
                      className="py-4 text-lg"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={retakePhoto}
                      className="bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Retake
                    </Button>
                    <Button
                      onClick={downloadPhoto}
                      className="bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-bold"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Save Photo
                    </Button>
                  </div>
                  
                  <Button
                    onClick={() => setShowFullResults(true)}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-xl font-bold"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Full Report
                  </Button>
                </div>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-full p-6">
                <div className="bg-red-600/95 backdrop-blur-md text-white p-4 rounded-xl shadow-2xl border-2 border-red-400 max-w-md">
                  <div className="font-bold text-lg mb-2">⚠️ Analysis Error</div>
                  <p className="text-sm mb-3">{error}</p>
                  <Button
                    onClick={retakePhoto}
                    className="w-full bg-white text-red-600 hover:bg-gray-100 font-bold"
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full transition-transform duration-300 ease-out"
          style={{ 
            transform: `scale(${zoomLevel})`,
            transformOrigin: 'center center',
            objectFit: 'cover',
            minWidth: '100%',
            minHeight: '100%'
          }}
        />
      )}
      
      <canvas ref={canvasRef} className="hidden" />
      
      {/* Error display for captured photo analysis */}
      {capturedImage && error && !isAnalyzing && (
        <div className="absolute bottom-20 left-0 right-0 z-[10001] px-4">
          <div className="bg-red-600/95 backdrop-blur-md text-white p-4 rounded-xl shadow-2xl border-2 border-red-400 max-w-md mx-auto">
            <div className="font-bold text-lg mb-2">⚠️ Analysis Error</div>
            <div className="text-sm">{error}</div>
            <Button
              onClick={() => analyzePhoto(capturedImage)}
              className="mt-3 bg-white text-red-600 hover:bg-gray-100 w-full font-bold"
            >
              Try Again
            </Button>
          </div>
        </div>
      )}
      
      {/* Brief instructions overlay - top, auto-hides */}
      {!capturedImage && showInstructions && (
        <div className="absolute top-20 left-0 right-0 flex justify-center z-[10001] px-4">
          <div className="bg-blue-600/90 backdrop-blur-sm text-white px-4 py-2 rounded-full shadow-lg text-sm font-medium max-w-md text-center">
            📸 Tap white button below to capture photo
          </div>
        </div>
      )}
      
      {/* Top controls */}
      <div 
        className="absolute top-0 left-0 right-0 p-4 z-[10000]"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)'
        }}
      >
        <div className="flex justify-between items-center">
          {/* Close Button - Left */}
          <Button
            variant="ghost"
            size="icon"
            onClick={stopCamera}
            className="text-white hover:bg-white/30 bg-black/60 backdrop-blur-md h-12 w-12 rounded-full border-2 border-white/30 transition-all duration-300 hover:scale-110 hover:border-white/50 shadow-lg"
          >
            <X className="w-6 h-6" />
          </Button>
          
          <div className="flex items-center gap-2">
            {isAnalyzing && (
              <div className="flex items-center gap-2 bg-blue-600/90 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg border border-blue-400/50">
                <Loader2 className="w-4 h-4 animate-spin" />
                Analyzing...
              </div>
            )}
            {capturedImage && !isAnalyzing && (
              <Button
                onClick={downloadPhoto}
                size="sm"
                className="bg-green-600/90 hover:bg-green-700 text-white rounded-full backdrop-blur-md border border-green-400/50 shadow-lg transition-all duration-300 hover:scale-105"
              >
                <Download className="w-4 h-4 mr-1" />
                Save Photo
              </Button>
            )}
          </div>
          
          {/* Flip Camera Button - Right (only when no captured image) */}
          {!capturedImage && (
            <Button
              variant="ghost"
              size="icon"
              onClick={switchCamera}
              className="text-white hover:bg-white/30 bg-black/60 backdrop-blur-md h-12 w-12 rounded-full border-2 border-white/30 transition-all duration-300 hover:scale-110 hover:border-white/50 shadow-lg hover:rotate-180"
            >
              <SwitchCamera className="w-6 h-6" />
            </Button>
          )}
          {capturedImage && <div className="w-12"></div>}
        </div>
      </div>
      
      {/* Zoom Controls - Right side (only for back camera and when no captured image) */}
      {!capturedImage && facingMode === 'environment' && (
        <div className="absolute right-6 top-1/2 transform -translate-y-1/2 z-[10000] flex flex-col gap-3">
          {[1, 2].map((level) => (
            <Button
              key={level}
              variant="ghost"
              size="icon"
              onClick={() => handleZoomChange(level)}
              className={`
                h-14 w-14 rounded-full font-bold text-sm transition-all duration-300
                ${zoomLevel === level 
                  ? 'bg-white text-black border-2 border-white shadow-2xl scale-110' 
                  : 'bg-black/60 text-white border-2 border-white/30 hover:bg-white/30 hover:border-white/50 hover:scale-105'
                }
                backdrop-blur-md shadow-lg
              `}
            >
              {level}x
            </Button>
          ))}
        </div>
      )}
      
      {/* Capture button - center bottom (only when no photo captured) - IMPROVED DESIGN */}
      {!capturedImage && (
        <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2 z-[10000]">
          <div className="flex flex-col items-center">
            <Button
              onClick={capturePhoto}
              size="lg"
              className="relative bg-white hover:bg-gray-100 text-black rounded-full h-24 w-24 shadow-2xl border-4 border-white/40 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(255,255,255,0.5)] active:scale-95"
            >
              <Camera className="w-12 h-12" />
              {/* Pulse animation ring */}
              <div className="absolute inset-0 rounded-full border-4 border-white animate-ping opacity-20"></div>
            </Button>
            <div className="text-white text-base font-semibold mt-4 bg-black/70 backdrop-blur-sm px-5 py-2 rounded-full shadow-lg border border-white/20">
              📸 Tap to Capture
            </div>
          </div>
        </div>
      )}
      
      {/* Old analytics overlay removed - now integrated into split layout above */}
      
      {/* Loading overlay when analyzing */}
      {capturedImage && isAnalyzing && (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-[10001] backdrop-blur-sm">
          <div className="text-white text-center bg-gradient-to-br from-blue-600 to-indigo-600 p-8 rounded-2xl shadow-2xl border border-blue-400/50 min-w-[320px]">
            <Loader2 className="w-16 h-16 animate-spin mx-auto mb-4 text-white" />
            <div className="text-xl font-bold mb-2">Analyzing Your Photo</div>
            <div className="text-sm text-blue-100 mb-4">Extracting emotional intelligence...</div>
            <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden">
              <div className="h-full bg-white animate-pulse" style={{width: '70%'}}></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveCamera;
